import classNames from 'classnames';
import { useState } from 'react';
import { ErrorMessage } from 'formik';

import { getCategoryName } from '../../../utils/common/category';
import analytics from '../../../services/analytics/index';
import useCommonStyles from '../commonStyles';
import TextLabel from '../TextLabel/TextLabel';
import { ReactComponent as RequiredErrorIcon } from '../../../svgIcons/required-error.svg';
import { ReactComponent as WarningIcon } from '../../../svgIcons/warning.svg';
import tooltipStyles from '../tooltipStyles';

import { IGenericInputProps } from './types';

function GenericInput({
  name,
  placeholder,
  textLabel,
  hasErrorValidation,
  selectedCategory,
  warningMessage,
  required,
  information,
  analyticsName,
  tooltipProps,
  inputType,
  inputMax,
  formProps: props,
}: IGenericInputProps) {
  const commonStyles = useCommonStyles();
  const tooltipClasses = tooltipStyles({ tooltipProps });
  const [changed, setChanged] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const inputClassName = classNames(
    commonStyles.inputClassName,
    hasErrorValidation &&
      props.errors[`${name}`] &&
      props.touched[`${name}`] &&
      commonStyles.inputError,
    showWarning && commonStyles.inputWarning,
  );

  const handleClick = () => {
    analytics.textBoxClick({
      text_box_name: analyticsName,
      category_name: getCategoryName(selectedCategory),
      page_path: window.location.pathname,
    });
  };

  const handleOnChange = (e) => {
    setChanged(true);
    props.handleChange(e);
  };

  const handleKeyDown = (e) => {
    if (inputType === 'number' && (e.key === 'e' || e.key === '-')) e.preventDefault();
  };

  const handleWarning = (e) => {
    if (e.target.value.length > 0 && e.target.value.length < 100) setShowWarning(true);
    else setShowWarning(false);
  };

  const handleOnBlur = (e) => {
    warningMessage != null && handleWarning(e);

    props.handleBlur(e);
    changed &&
      analytics.textboxType({
        text_box_name: analyticsName,
        category_name: getCategoryName(selectedCategory),
        page_path: window.location.pathname,
      });
  };

  return (
    <div>
      <TextLabel
        tooltipClassName={tooltipClasses.tooltip}
        arrowClassName={tooltipClasses.tooltipArrow}
        required={required}
        htmlFor={name}
        label={textLabel}
        infoText={information}
      />
      <input
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        className={inputClassName}
        name={name}
        type={inputType ?? 'text'}
        min={0}
        max={inputMax}
        placeholder={placeholder}
        value={props.values[`${name}`]}
        onBlur={(e) => handleOnBlur(e)}
        onChange={(e) => handleOnChange(e)}
      />
      {(hasErrorValidation || warningMessage) && (
        <div className={commonStyles.errorContainer}>
          {hasErrorValidation && (
            <ErrorMessage
              name={`${name}`}
              render={(msg) => (
                <div className={commonStyles.messageText}>
                  <RequiredErrorIcon className={commonStyles.messageIcon} />
                  {msg}
                </div>
              )}
            />
          )}
          {showWarning && (
            <span className={classNames(commonStyles.messageText, commonStyles.warning)}>
              <WarningIcon className={commonStyles.messageIcon} />
              {warningMessage}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
export default GenericInput;
